import React from "react";
import moment from "moment";
import styled, { css } from "styled-components";
import { Row, Col, Progress, Tooltip, Tag, Spin } from "antd";
import { down } from "styled-breakpoints";
import { useFetchLocalStorage } from "../../lib/useFetchLocalStorage";
import useUser from "../../lib/useUser";
import useJobData from "hooks/api/useJobData";
import * as ROUTES from "constants/routes";
import CustomLink from "components/shared/CustomLink";
import AllJobsTable from "components/job/shared/table/allJobsTable";

export const Job = () => {
  useFetchLocalStorage();
  useUser({ redirectTo: "/login" });

  // Component State
  const [pagePagination, setPagePagination] = React.useState({
    pageIndex: 1,
    pageSize: 30,
  });

  // console.log("data :>> ", data);

  // const serverData = makeData(10000);
  const { data: serverData, status } = useJobData({ pagePagination });

  const columns = React.useMemo(
    () => [
      {
        Header: "Title",
        accessor: "name",
        width: 200,
        Cell: (props) => {
          return (
            <CustomLink
              href={ROUTES.JOBS_DETAIL}
              as={`/jobs/${props.row.original.id}`}
            >
              <StyledTitle>{props.value}</StyledTitle>
              <StyledCreatedAt>
                <span className="value">
                  {moment(props.row.original.created_at).format("DD MMM YY")}
                </span>
              </StyledCreatedAt>
            </CustomLink>
          );
        },
      },
      {
        Header: "Client",
        accessor: "client",
        width: 50,
        Cell: ({ value }) => {
          return <Tooltip title={value.name}>{value.short_name}</Tooltip>;
        },
      },
      {
        Header: "Quantity",
        accessor: "quantity",
        isVisible: true,
        width: 30,
      },
      {
        Header: "Status",
        accessor: "status",
        isVisible: true,
        width: 30,
        Cell: ({ value }) => {
          return (
            <Tag
              style={{ marginRight: 0 }}
              color={
                value === "new"
                  ? "blue"
                  : value === "completed"
                  ? "#cbd5e0"
                  : value === "stuck"
                  ? "red"
                  : value === "started"
                  ? "green"
                  : "purple"
              }
            >
              {value}
            </Tag>
          );
        },
      },
      {
        Header: "Type",
        accessor: "type.name",
        isVisible: true,
        width: 50,
      },
      {
        Header: "Completion",
        accessor: "dateline",
        Cell: ({ value }) => {
          if (value.length > 0) {
            const datelineCompleted = value.filter(
              (dateline) => dateline.completed
            );
            const completionPercentage = Math.round(
              ((datelineCompleted.length / value.length) * 100).toFixed(2)
            );
            return (
              <Progress
                percent={completionPercentage}
                strokeWidth={12}
                showInfo={false}
                trailColor="#e2e8f0"
                size="small"
              />
            );
          } else {
            return "No dateline";
          }
        },
        isVisible: true,
        width: 30,
      },
      {
        Header: "Urgent",
        accessor: "general.urgent",
        width: 60,
        Cell: ({ value }) => {
          return value ? (
            <div
              style={{
                width: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✅
            </div>
          ) : (
            <div
              style={{
                width: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ❌
            </div>
          );
        },
        isVisible: true,
        textAlign: "center",
      },
      {
        Header: "Job Sheets",
        accessor: "job_sheets",
        Cell: ({ value }) => {
          return <div style={{ textAlign: "center" }}>{value.length}</div>;
        },
        textAlign: "center",
        isVisible: true,
        width: 50,
      },
      {
        Header: "Order Approval",
        accessor: "approval.order_at",
        isVisible: true,
        textAlign: "center",
        Cell: ({ value }) => {
          return value ? (
            <div style={{ textAlign: "center" }}>✅</div>
          ) : (
            <div style={{ textAlign: "center" }}>❌</div>
          );
        },
        width: 30,
      },
      {
        Header: "CTP Approval",
        accessor: "approval.ctp_at",
        isVisible: true,
        textAlign: "center",
        Cell: ({ value }) => {
          return value ? (
            <div style={{ textAlign: "center" }}>✅</div>
          ) : (
            <div style={{ textAlign: "center" }}>❌</div>
          );
        },
        width: 30,
      },
    ],
    []
  );

  const fetchData = React.useCallback(async ({ pageSize, pageIndex }) => {
    setPagePagination((prevState) => ({
      ...prevState,
      pageSize,
      pageIndex,
    }));
  }, []);

  return (
    <>
      <StyledContainer>
        <Row gutter={16} justify="space-around">
          <Col span={4}>
            <StyledStats>
              <h5 className="title">Completed</h5>
              {status === "loading" || status === "idle" ? (
                <SpinContainer>
                  <Spin />
                </SpinContainer>
              ) : (
                <h2 className="stats">
                  {serverData.jobStatusCount.completedJob}
                </h2>
              )}
            </StyledStats>
          </Col>
          <Col span={4}>
            <StyledStats>
              <h5 className="title">New</h5>
              {status === "loading" || status === "idle" ? (
                <SpinContainer>
                  <Spin />
                </SpinContainer>
              ) : (
                <h2 className="stats">{serverData.jobStatusCount.newJob}</h2>
              )}
            </StyledStats>
          </Col>
          <Col span={4}>
            <StyledStats>
              <h5 className="title">Running</h5>
              {status === "loading" || status === "idle" ? (
                <SpinContainer>
                  <Spin />
                </SpinContainer>
              ) : (
                <h2 className="stats">
                  {serverData.jobStatusCount.startedJob}
                </h2>
              )}
            </StyledStats>
          </Col>
          <Col span={4}>
            <StyledStats>
              <h5 className="title">Stuck</h5>
              {status === "loading" || status === "idle" ? (
                <SpinContainer>
                  <Spin />
                </SpinContainer>
              ) : (
                <h2 className="stats">{serverData.jobStatusCount.stuckJob}</h2>
              )}
            </StyledStats>
          </Col>
          <Col span={4}>
            <StyledStats>
              <h5 className="title">Cancelled</h5>
              {status === "loading" || status === "idle" ? (
                <SpinContainer>
                  <Spin />
                </SpinContainer>
              ) : (
                <h2 className="stats">
                  {serverData.jobStatusCount.cancelledJob}
                </h2>
              )}
            </StyledStats>
          </Col>
        </Row>
      </StyledContainer>
      <StyledContainer>
        <Styles>
          <AllJobsTable
            columns={columns}
            data={serverData?.data || []}
            fetchData={fetchData}
            status={status}
            resultCount={serverData?.resultCount}
            pageCount={serverData?.pageCount}
          />
        </Styles>
      </StyledContainer>
    </>
  );
};

const Styles = styled.div`
  table {
    width: 100%;

    th {
      padding: 0.6rem;
      padding-left: 0.5rem;
      background-color: ${(props) => props.theme.colors.grey[300]};
      color: ${(props) => props.theme.colors.grey[600]};
      font-weight: 500;
    }

    th:first-child {
      border-radius: 10px 0 0 10px;
    }

    th:last-child {
      border-radius: 0 10px 10px 0;
    }

    tbody:before {
      content: "@";
      display: block;
      line-height: ${(props) => props.theme.spacing[1]};
      text-indent: -99999px;
    }

    tr {
      &:hover {
        background-color: ${(props) => props.theme.colors.grey[200]};
      }
    }

    td {
      padding: ${(props) => props.theme.spacing[2]};
      padding-left: 0.5rem;
      border: none;
      vertical-align: middle;
      border-bottom: 1px solid ${(props) => props.theme.colors.grey[300]};
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .pagination {
    padding: 0.5rem;
    padding-left: 0;
  }
`;

const StyledContainer = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  padding: ${(props) => props.theme.spacing[2]};
  border-radius: ${(props) => props.theme.spacing[1]};
  border: 1px solid ${(props) => props.theme.colors.grey[400]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
  transition: background-color 0.5s ease;
  box-sizing: border-box;
`;

const StyledTitle = styled.p`
  font-weight: 500;
  margin: 0;
  padding: 0;
`;

const StyledCreatedAt = styled.div`
  .label {
    font-size: ${(props) => props.theme.fontSize["xs"]};
    color: ${(props) => props.theme.colors.grey[500]};
  }

  .value {
    font-size: ${(props) => props.theme.fontSize["xs"]};
    color: ${(props) => props.theme.colors.grey[500]};
  }
`;

const StyledStats = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;

  .title {
    font-size: 1em;
    text-transform: uppercase;
    color: ${(props) => props.theme.colors.green[500]};
    font-weight: 600;
    margin: 0;
    padding: 0;

    ${down("sm")} {
      font-size: 0.7em;
    }
  }

  .stats {
    font-size: 2em;
    margin: 0;
    padding: 0;

    ${down("sm")} {
      font-size: 1.2em;
    }
  }
`;

const SpinContainer = styled.div`
  margin-top: ${(props) => props.theme.spacing[2]};
`;

export default Job;
